/**
 * Berkwellness — Product & Collection Page Enhancements v2
 * Scroll reveal, grid stagger, 3D tilt, thumbnail scroll, variant ripple, header parallax
 */
(function () {
  'use strict';

  /* ==========================================================================
     Scroll Reveal — IntersectionObserver
     ========================================================================== */
  function initScrollReveal() {
    var revealElements = document.querySelectorAll('.neon-reveal');
    if (!revealElements.length) return;

    if (!('IntersectionObserver' in window)) {
      revealElements.forEach(function (el) {
        el.classList.add('neon-reveal--visible');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('neon-reveal--visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
    );

    revealElements.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ==========================================================================
     Product Grid Stagger
     ========================================================================== */
  function initGridStagger() {
    var gridItems = document.querySelectorAll('[data-neon-page].collection-page .product-grid__item');
    if (!gridItems.length) return;

    if (window.matchMedia('(max-width: 749px)').matches) {
      gridItems.forEach(function (item) {
        item.classList.add('neon-reveal--visible');
      });
      return;
    }

    if (!('IntersectionObserver' in window)) {
      gridItems.forEach(function (item) {
        item.classList.add('neon-reveal--visible');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('neon-reveal--visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05, rootMargin: '0px 0px -20px 0px' }
    );

    gridItems.forEach(function (item) {
      observer.observe(item);
    });
  }

  /* ==========================================================================
     3D Card Tilt (desktop only, pointer: fine)
     ========================================================================== */
  function initCardTilt() {
    if (!window.matchMedia('(any-pointer: fine)').matches) return;

    var cards = document.querySelectorAll('[data-neon-page].collection-page .product-card');
    var MAX_TILT = 2;

    cards.forEach(function (card) {
      var rafId = null;
      var targetX = 0;
      var targetY = 0;
      var currentX = 0;
      var currentY = 0;
      var isHovered = false;

      function lerp(a, b, t) {
        return a + (b - a) * t;
      }

      function animate() {
        currentX = lerp(currentX, targetX, 0.12);
        currentY = lerp(currentY, targetY, 0.12);

        card.style.transform = 'perspective(800px) rotateX(' + currentX + 'deg) rotateY(' + currentY + 'deg)';

        if (isHovered || Math.abs(currentX) > 0.01 || Math.abs(currentY) > 0.01) {
          rafId = requestAnimationFrame(animate);
        } else {
          card.style.transform = '';
          rafId = null;
        }
      }

      card.addEventListener('mouseenter', function () {
        isHovered = true;
        card.style.zIndex = '2';
        card.style.willChange = 'transform';
        if (!rafId) rafId = requestAnimationFrame(animate);
      });

      card.addEventListener('mousemove', function (e) {
        var rect = card.getBoundingClientRect();
        var relX = (e.clientX - rect.left) / rect.width - 0.5;
        var relY = (e.clientY - rect.top) / rect.height - 0.5;
        targetY = relX * MAX_TILT * 2;
        targetX = -relY * MAX_TILT * 2;
      });

      card.addEventListener('mouseleave', function () {
        isHovered = false;
        targetX = 0;
        targetY = 0;
        card.style.zIndex = '';
        setTimeout(function () {
          card.style.willChange = '';
        }, 500);
        if (!rafId) rafId = requestAnimationFrame(animate);
      });
    });
  }

  /* ==========================================================================
     Collection Header Parallax Fade
     ========================================================================== */
  function initHeaderParallax() {
    var header = document.querySelector('[data-neon-page].collection-page .collection-header-bar__title');
    if (!header) return;
    if (window.matchMedia('(max-width: 749px)').matches) return;

    var ticking = false;

    function updateParallax() {
      var rect = header.getBoundingClientRect();
      var viewH = window.innerHeight;
      var progress = Math.max(0, Math.min(1, 1 - (rect.bottom / viewH)));
      var opacity = 1 - progress * 1.6;
      var translateY = progress * -40;

      header.style.opacity = Math.max(0, opacity);
      header.style.transform = 'translateY(' + translateY + 'px)';
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(updateParallax);
        ticking = true;
      }
    }, { passive: true });
  }

  /* ==========================================================================
     Thumbnail Scroll — click thumbnail scrolls to main media
     ========================================================================== */
  function initThumbnailScroll() {
    var thumbnails = document.querySelectorAll('[data-neon-page].product-page .thumbnail, [data-neon-page].product-page .thumbnail-list li');
    if (!thumbnails.length) return;

    thumbnails.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var mainMedia = document.querySelector('[data-neon-page].product-page .product-media-gallery__media:not([hidden])');
        if (mainMedia) {
          mainMedia.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      });
    });
  }

  /* ==========================================================================
     Variant Button Ripple — subtle pulse on selection
     ========================================================================== */
  function initVariantRipple() {
    var variantButtons = document.querySelectorAll('[data-neon-page].product-page .variant-picker button, [data-neon-page].product-page .variant-option__button-label');
    if (!variantButtons.length) return;

    variantButtons.forEach(function (btn) {
      var observer = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          if (mutation.type === 'attributes') {
            var isSelected = btn.getAttribute('aria-checked') === 'true'
              || btn.getAttribute('aria-selected') === 'true'
              || btn.classList.contains('active');

            if (isSelected) {
              btn.style.transform = 'scale(0.96)';
              requestAnimationFrame(function () {
                btn.style.transition = 'transform 200ms cubic-bezier(0.16, 1, 0.3, 1)';
                btn.style.transform = 'scale(1)';
              });
            }
          }
        });
      });

      observer.observe(btn, { attributes: true, attributeFilter: ['aria-checked', 'aria-selected', 'class'] });
    });
  }

  /* ==========================================================================
     Hash Scroll
     ========================================================================== */
  function initHashScroll() {
    var url = new URL(window.location.href);
    if (url.hash) {
      document.addEventListener('DOMContentLoaded', function () {
        var card = document.getElementById(url.hash.slice(1));
        if (card) {
          card.scrollIntoView({ behavior: 'instant' });
        }
      }, { once: true });
    }
  }

  /* ==========================================================================
     Initialize
     ========================================================================== */
  function init() {
    initScrollReveal();
    initGridStagger();
    initCardTilt();
    initHeaderParallax();
    initThumbnailScroll();
    initVariantRipple();
    initHashScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function () {
    setTimeout(init, 100);
  });
})();
